import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { useApiClient } from '@/api/apiClientContext'

export const queryKeys = {
  calendarIntegrationStatus: (tenantId: string, userId: string) => [
    'calendar-integration-status',
    tenantId,
    userId,
  ],
}

export const useCalendarIntegrationStatus = (userId: string, tenantId: string) => {
  const { externalCalendarV3 } = useApiClient()

  return useQuery({
    queryKey: queryKeys.calendarIntegrationStatus(tenantId, userId),
    queryFn: () => externalCalendarV3.calendarIntegrationStatusV3(tenantId, { userId }),
  })
}

export const useConnectGoogleSecondaryAccount = (tenantId: string) => {
  const { googleAuthenticationV3 } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, redirect }: { userId: string; redirect: string }) =>
      googleAuthenticationV3.connectASecondaryAccountToGoogle(tenantId, { userId, redirect }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calendarIntegrationStatus(tenantId, variables.userId),
      })
    },
  })
}

export const useConnectMicrosoftSecondaryAccount = (tenantId: string) => {
  const { microsoftAuthenticationV3 } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, redirect }: { userId: string; redirect: string }) =>
      microsoftAuthenticationV3.connectASecondaryAccountToMicrosoft(tenantId, { userId, redirect }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calendarIntegrationStatus(tenantId, variables.userId),
      })
    },
  })
}

export const useDisconnectGoogleSecondaryCalendarAccount = (tenantId: string) => {
  const { googleAuthenticationV2 } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, accountId }: { userId: string; accountId: string }) =>
      googleAuthenticationV2.disconnectSecondaryCalendarFromGoogle(tenantId, {
        user_id: userId,
        account_id: accountId,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calendarIntegrationStatus(tenantId, variables.userId),
      })
    },
  })
}

export const useDisconnectMicrosoftSecondaryCalendarAccount = (tenantId: string) => {
  const { microsoftAuthentication } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, accountId }: { userId: string; accountId: string }) =>
      microsoftAuthentication.disconnectSecondaryCalendarFromMicrosoft(tenantId, {
        user_id: userId,
        account_id: accountId,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calendarIntegrationStatus(tenantId, variables.userId),
      })
    },
  })
}

export const useDisconnectGoogleIntegration = () => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const { googleAuthenticationV2 } = useApiClient()

  return useMutation({
    mutationFn: (data: { connectionType: 'ind'; googleId: string } | { connectionType: 'org' }) => {
      const credentialRef =
        data.connectionType === 'org' ? `org:${tenantId}` : `ind:${tenantId}:${data.googleId}`
      return googleAuthenticationV2.disconnectAnAccountFromGoogle(tenantId, credentialRef)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calendarIntegrationStatus(tenantId, userId),
      })
    },
  })
}

export const useDisconnectMicrosoftIntegration = () => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const { microsoftAuthentication } = useApiClient()

  return useMutation({
    mutationFn: (
      data: { connectionType: 'ind'; microsoftId: string } | { connectionType: 'org' }
    ) => {
      const credentialRef =
        data.connectionType === 'org' ? `org:${tenantId}` : `ind:${tenantId}:${data.microsoftId}`
      return microsoftAuthentication.disconnectAnAccountFromMicrosoft(tenantId, credentialRef)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calendarIntegrationStatus(tenantId, userId),
      })
    },
  })
}

export const useConnectGoogleIntegration = (redirectURL: string) => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const { googleAuthenticationV3 } = useApiClient()

  return useMutation({
    mutationFn: () => {
      return googleAuthenticationV3.connectIndividualAccountToGoogle(tenantId, {
        userId,
        redirect: redirectURL,
      })
    },
    onSuccess: async data => {
      await queryClient.resetQueries({
        queryKey: queryKeys.calendarIntegrationStatus(tenantId, userId),
      })

      if ('location' in data) {
        window.location.assign(data.location)
      }
    },
  })
}

export const useConnectMicrosoftIntegration = (redirectURL: string) => {
  const queryClient = useQueryClient()
  const { tenantId, userId } = useFireSessionContext()
  const { microsoftAuthenticationV3 } = useApiClient()

  return useMutation({
    mutationFn: () => {
      return microsoftAuthenticationV3.connectIndividualAccountToMicrosoft(tenantId, {
        userId,
        redirect: redirectURL,
      })
    },
    onSuccess: async data => {
      await queryClient.resetQueries({
        queryKey: queryKeys.calendarIntegrationStatus(tenantId, userId),
      })

      if ('location' in data) {
        window.location.assign(data.location)
      }
    },
  })
}
